import { createSlice } from '@reduxjs/toolkit';
import {
    addFileCommentThunk,
    assignFileThunk,
    createDocFileThunk,
    createFileThunk,
    deleteFileCommentThunk,
    deleteFileTaskThunk,
    deleteFileThunk,
    editFileNameThunk,
    editWordDocumentThunk,
    fetchFilesThunk,
    fetchFolderFilesThunk,
    fetchRecentFilesThunk,
    fetchSingleFileDetailsThunk,
    getWordDocumentThunk,
    removeAssingFileThunk
} from '../thunks/file';
import { FileInfoObject, FileObject, FileState } from '../../params/redux/file';

// Initial state with default values for authentication and user info
const initialState: FileState = {
    files: [],
    selectedFile: null,
    selectedFileDetail: null,
    loaders: {
        isLoading: false,
        isCreating: false,
        isUpdating: false,
        isDeleting: false,
        isSearching: false,
        isInserting: false
    },
    editFileDoc: null
};

// Slice for app-related states and actions
const fileSlice = createSlice({
    // Slice name
    name: 'file',
    // inital state for the slice
    initialState,
    reducers: {
        settingSelectedFile: (state, action) => {
            state.selectedFile = action.payload;
        },
        settingSelectedFileDetail: (state, action) => {
            state.selectedFileDetail = action.payload;
        },
        // Resets loading states related to app actions
        resettingFileLoader: (state) => {
            state.loaders = initialState.loaders;
        },
        // Resets all app data to the initial state
        resettingFileData: () => initialState
    },
    extraReducers: (builder) => {
        // Handle async actions for login
        builder
            .addCase(fetchFilesThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
            })
            .addCase(fetchFilesThunk.fulfilled, (state, action) => {
                state.files = action.payload as FileObject[];
            })
            .addCase(fetchFilesThunk.rejected, (state) => {
                state.files = [];
            });
        builder
            .addCase(fetchFolderFilesThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
            })
            .addCase(fetchFolderFilesThunk.fulfilled, (state, action) => {
                state.files = action.payload as FileObject[];
            })
            .addCase(fetchFolderFilesThunk.rejected, (state) => {
                state.files = [];
            });
        builder
            .addCase(createFileThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isCreating = true;
            })
            .addCase(createFileThunk.fulfilled, () => {
                //
            })
            .addCase(createFileThunk.rejected, () => {
                //
            });
        builder
            .addCase(createDocFileThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isCreating = true;
            })
            .addCase(createDocFileThunk.fulfilled, () => {
                //
            })
            .addCase(createDocFileThunk.rejected, () => {
                //
            });
        builder
            .addCase(deleteFileThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isDeleting = true;
            })
            .addCase(deleteFileThunk.fulfilled, () => {
                //
            })
            .addCase(deleteFileThunk.rejected, () => {
                //
            });
        builder
            .addCase(fetchRecentFilesThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
            })
            .addCase(fetchRecentFilesThunk.fulfilled, (state, action) => {
                state.files = action.payload as FileObject[];
            })
            .addCase(fetchRecentFilesThunk.rejected, (state) => {
                state.files = [];
            });

        builder
            .addCase(fetchSingleFileDetailsThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isLoading = true;
            })
            .addCase(fetchSingleFileDetailsThunk.fulfilled, (state, action) => {
                state.selectedFileDetail = action.payload as FileInfoObject;
            })
            .addCase(fetchSingleFileDetailsThunk.rejected, (state) => {
                state.selectedFileDetail = null;
            });
        builder
            .addCase(addFileCommentThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isUpdating = true;
            })
            .addCase(addFileCommentThunk.fulfilled, () => {
                //
            })
            .addCase(addFileCommentThunk.rejected, () => {
                //
            });
        builder
            .addCase(deleteFileTaskThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isUpdating = true;
            })
            .addCase(deleteFileTaskThunk.fulfilled, () => {
                //
            })
            .addCase(deleteFileTaskThunk.rejected, () => {
                //
            });
        builder
            .addCase(deleteFileCommentThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isUpdating = true;
            })
            .addCase(deleteFileCommentThunk.fulfilled, () => {
                //
            })
            .addCase(deleteFileCommentThunk.rejected, () => {
                //
            });
        builder
            .addCase(editFileNameThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isUpdating = true;
            })
            .addCase(editFileNameThunk.fulfilled, () => {
                //
            })
            .addCase(editFileNameThunk.rejected, () => {
                //
            });
        builder
            .addCase(assignFileThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isUpdating = true;
            })
            .addCase(assignFileThunk.fulfilled, () => {
                //
            })
            .addCase(assignFileThunk.rejected, () => {
                //
            });
        builder
            .addCase(removeAssingFileThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isUpdating = true;
            })
            .addCase(removeAssingFileThunk.fulfilled, () => {
                //
            })
            .addCase(removeAssingFileThunk.rejected, () => {
                //
            });
        builder
            .addCase(editWordDocumentThunk.pending, (state) => {
                // Set loading flag during login request
                state.loaders.isUpdating = true;
            })
            .addCase(editWordDocumentThunk.fulfilled, () => {
                //
            })
            .addCase(editWordDocumentThunk.rejected, () => {
                //
            });
        builder
            .addCase(getWordDocumentThunk.pending, (state) => {
                state.loaders.isLoading = true;
            })
            .addCase(getWordDocumentThunk.fulfilled, (state, action) => {
                if (state.selectedFileDetail) {
                    state.selectedFileDetail = {
                        ...state.selectedFileDetail,
                        html: action.payload as string
                    };
                }
            })
            .addCase(getWordDocumentThunk.rejected, () => {
                //
            });
    }
});

// Exporting actions for updating app state
export const {
    settingSelectedFileDetail,
    settingSelectedFile,
    resettingFileData,
    resettingFileLoader
} = fileSlice.actions;

// Exporting the reducer to be used in the store
export default fileSlice.reducer;
